import { router, Stack, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

export interface WalletPaySuccessAnalytics {
  screenKey: string;
  eventName: string;
  category: string;
  flowName: string;
}

export interface WalletPaySuccessProps {
  analytics: WalletPaySuccessAnalytics;
  visual?: IllustrationKeyType;
  onSuccess?: () => void | Promise<void>;
  skipAutoNavigate?: boolean;
}

const navigateBack = (): void => {
  if (router.canGoBack()) {
    router.back();
  } else {
    router.replace('/');
  }
};

const WalletPaySuccess = ({
  analytics,
  visual = 'PLANE_A_ANIMATED',
  onSuccess,
  skipAutoNavigate = false,
}: WalletPaySuccessProps) => {
  const { screenKey, eventName, category, flowName } = analytics;

  const { t: translatedText } = useTranslation('patterns', {
    keyPrefix: 'walletPay.successScreen',
  });
  const { amount } = useLocalSearchParams<{
    amount?: string;
  }>();

  const numericAmount = useMemo(
    () => (amount ? parseFloat(amount) : 0),
    [amount],
  );

  // Defensive: Redirect if amount is missing or invalid
  useEffect(() => {
    if (numericAmount <= 0) {
      navigateBack();
    }
  }, [numericAmount]);

  const { refreshTiles, data, fetching, error } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_ADD_MONEY_DDA,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_WELCOME,
  ]);

  useEffect(() => {
    if (error && !fetching) {
      // TODO(JT-145384): Handle tile refresh error (show toast or log)
    }
    if ((data?.tiles || error) && !fetching) {
      if (!skipAutoNavigate) {
        navigateBack();
      }
    }
  }, [data, error, fetching, skipAutoNavigate]);

  useViewDidFocusScreenAnalyticsEvent({
    key: screenKey,
    customAttributes: {
      amount: amount ?? '0',
      flow_name: flowName,
    },
  });

  const { segmentClient } = useAnalyticsConfiguration();
  const hasLoggedCompletedRef = useRef(false);

  useEffect(() => {
    if (numericAmount <= 0 || hasLoggedCompletedRef.current) {
      return;
    }

    void segmentClient.logEvent(eventName, {
      category,
      amount: numericAmount,
    });
    hasLoggedCompletedRef.current = true;
  }, [numericAmount, segmentClient, eventName, category]);

  const formattedAmount = formatCurrency(amount?.toString() ?? '0', true);

  const handleDonePress = (): void => {
    if (onSuccess) {
      void onSuccess();
    } else {
      refreshTiles();
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerBackVisible: false,
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('buttons.done'),
          onPress: handleDonePress,
          isLoading: fetching,
        }}
        eyebrowText={translatedText('eyebrowText')}
        visual={visual}
        titleText={translatedText('title', {
          amount: formattedAmount,
        })}
        content={<BodyBaseText>{translatedText('subtitle')}</BodyBaseText>}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default WalletPaySuccess;
